import { useSearchParams } from 'react-router-dom'
import { PolicyDecisionsPanel, type LedgerFilterState } from '@/features/policies/components/PolicyDecisionsPanel'
import { VERDICT_FILTERS, INTERACTION_FILTERS, type VerdictFilter, type InteractionFilter } from '@/features/policies/lib/decisionView'
import s from '@/shared/styles/policyTabs.module.css'
import { TabButton } from '@/features/policies/components/TabButton'
import { ArtefactPanel } from '@/features/policies/tabs/ArtefactPanel'
import { OverviewPanel } from '@/features/policies/tabs/OverviewPanel'
import { POLICY_SCOPES, TAB_KEYS, type Tab } from '@/features/policies/tabs/policyScopes'

/**
 * The URL is the single source of truth for the tab and for the ledger's filters.
 *
 * It used to be useState, which meant the Audit Log tab could not be linked at all - every share
 * of "look at this refusal" landed the reader on Overview. A ledger gets quoted in support and
 * audit conversations, so it has to be addressable.
 *
 * Every write is `replace: true` except the tab itself: narrowing a filter should not fill the back
 * stack, while Back from a tab should leave the page rather than walking through filter states.
 */
export function PolicyTabs({ orgId, canAdmin, isOwner, canOfferBaseline }: {
  orgId: string; canAdmin: boolean; isOwner: boolean; canOfferBaseline: boolean
}) {
  const [params, setParams] = useSearchParams()

  const raw = params.get('tab')
  // An unknown value falls back rather than rendering nothing, so a stale link still works.
  const tab: Tab = TAB_KEYS.includes(raw as Tab) ? (raw as Tab) : 'overview'

  function setTab(next: Tab) {
    const p = new URLSearchParams(params)
    if (next === 'overview') p.delete('tab')
    else p.set('tab', next)
    // The ledger's filters mean nothing on another tab, and leaving them behind would make a later
    // return to the Audit Log silently pre-filtered by something the reader never set.
    for (const k of ['verdict', 'interaction', 'policy', 'q', 'req']) p.delete(k)
    setParams(p)
  }

  /**
   * The Overview's ranked list promises "show me this rule's refusals", and the rows that answer it
   * are a tab away. One write, so the tab and the filter land together: setTab clears `policy` on
   * purpose, so calling the two in sequence would arrive unfiltered.
   */
  function openDecisionsForPolicy(policyId: string) {
    const p = new URLSearchParams(params)
    p.set('tab', 'decisions')
    for (const k of ['verdict', 'interaction', 'q', 'req']) p.delete(k)
    p.set('policy', policyId)
    setParams(p)
  }

  const verdictRaw = params.get('verdict')
  const interactionRaw = params.get('interaction')
  const filters: LedgerFilterState = {
    verdict: VERDICT_FILTERS.includes(verdictRaw as VerdictFilter) ? (verdictRaw as VerdictFilter) : 'all',
    interaction: INTERACTION_FILTERS.includes(interactionRaw as InteractionFilter)
      ? (interactionRaw as InteractionFilter)
      : 'all',
    policyId: params.get('policy'),
    q: params.get('q') ?? '',
    // Arrives from a refused `terraform apply`, whose terminal output links here. The CLI shows
    // the value as `POLICY-BLOCK-<req_id>` (it is terraform's Lock Info `ID`), so a pasted id is
    // accepted as readily as a followed link - the worker strips the prefix too, but doing it
    // here keeps the chip and the request agreeing on what is being filtered.
    reqId: (params.get('req') ?? '').replace(/^POLICY-BLOCK-/, '') || null,
  }

  function setFilters(next: Partial<LedgerFilterState>) {
    const p = new URLSearchParams(params)
    const write = (key: string, value: string | null, isDefault: boolean) => {
      if (value === null || value === '' || isDefault) p.delete(key)
      else p.set(key, value)
    }
    if ('verdict' in next) write('verdict', next.verdict ?? null, next.verdict === 'all')
    if ('interaction' in next) write('interaction', next.interaction ?? null, next.interaction === 'all')
    if ('policyId' in next) write('policy', next.policyId ?? null, false)
    if ('q' in next) write('q', next.q ?? null, false)
    if ('reqId' in next) write('req', next.reqId ?? null, false)
    setParams(p, { replace: true })
  }

  return (
    <div className={s.section}>
      <div className={s.tabBar} role="tablist" aria-label="Policy scopes">
        <TabButton label="Overview" active={tab === 'overview'} onClick={() => setTab('overview')} />
        {/* Immediately after Overview, because it is the detail behind everything Overview
            summarises: the individual rows, filterable, each naming the rule that refused it. */}
        {/* Labelled "Audit Log"; the slug stays `decisions` because the worker builds the
            refused-apply link in terraform's Lock Info from it (lib/lockInfoRefusal.ts). */}
        <TabButton label="Audit Log" active={tab === 'decisions'} onClick={() => setTab('decisions')} />
        {POLICY_SCOPES.map((a) => (
          <TabButton
            key={a.key}
            label={a.label}
            active={tab === a.key}
            hint={a.live ? undefined : 'preview'}
            onClick={() => setTab(a.key)}
          />
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewPanel
          orgId={orgId}
          canAdmin={canAdmin}
          canOfferBaseline={canOfferBaseline}
          onJump={(k) => setTab(k)}
          onPickPolicy={openDecisionsForPolicy}
        />
      )}

      {tab === 'decisions' && (
        <PolicyDecisionsPanel orgId={orgId} filters={filters} onFilters={setFilters} />
      )}

      {POLICY_SCOPES.filter((a) => a.key === tab).map((a) => (
        <ArtefactPanel key={a.key} orgId={orgId} canAdmin={canAdmin} isOwner={isOwner} artefact={a} />
      ))}
    </div>
  )
}

